import { useState } from "react";
import { Alert, Button, Icon, Modal, Spinner, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useRequestPasswordResetMutation } from "@/redux/apis/auth/authApi";
import { useProfileContext } from "../context";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Operator-initiated password change for a driver. Mirrors ProfilePasswordModal
 * (operator changing their own password) — same Kinde flow, same two-step
 * confirm — but the *driver* is the one signed out and redirected to set a new
 * password; the operator stays signed in.
 *
 * On confirm: POST /v1/auth/password-reset/{driver_uuid}/request — Kinde marks
 * the account for password reset and v2 invalidates the driver's active sessions
 * immediately. The driver's next login goes through Kinde's create-new-password
 * flow.
 *
 * Audit (written by v2):
 *   - driver.password.reset_requested    (actor = operator)
 *   - driver.sessions.invalidated        (actor = operator)
 *   - driver.password.changed            (actor = null/system, fired when the
 *                                          driver actually completes the reset
 *                                          via Kinde's hosted UI)
 */
export const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const { driver } = useProfileContext();
  const [requestPasswordReset, { isLoading }] = useRequestPasswordResetMutation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChangePasswordClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    try {
      setErrors([]);
      await requestPasswordReset(driver.uuid).unwrap();
      setShowConfirmation(false);
      onClose();
    } catch (e) {
      setErrors(getErrorMessages(e as Error));
      setShowConfirmation(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setErrors([]);
    onClose();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setErrors([]);
    onClose();
  };

  return (
    <>
      {/* Main Modal */}
      <Modal open={open && !showConfirmation} onClose={handleClose} variant="web-small">
        <div className="w-full px-6 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <div className="mb-4 flex items-center justify-center rounded-lg bg-primary-light p-3">
                <Icon name="Lock1" variant="Bold" size={36} className="text-primary" />
              </div>
            </div>
            <Typography variant="h1">Change Password</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              The driver will be logged out. The next time they sign in, they&apos;ll be prompted to set a new password.
            </Typography>
          </div>

          {errors.length > 0 && (
            <div className="my-4">
              {errors.map((e, i) => (
                <Alert key={i} type="danger" message={e} className="my-4" />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse gap-3 md:flex-row">
            <Button onClick={handleClose} variant="secondary" className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleChangePasswordClick} className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal open={showConfirmation} onClose={handleCancelConfirmation} variant="web-small">
        <div className="w-full px-6 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <div className="mb-4 flex items-center justify-center rounded-lg bg-warning-light p-3">
                <Icon name="Warning2" variant="Bold" size={36} className="text-warning" />
              </div>
            </div>
            <Typography variant="h1">Confirm Password Change</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              The driver will be logged out, and the next time they sign in they&apos;ll be prompted to set a new password. Are you sure you want to continue?
            </Typography>
          </div>

          <div className="mt-8 flex flex-col-reverse gap-3 md:flex-row">
            <Button onClick={handleCancelConfirmation} variant="secondary" className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isLoading} className="w-full md:flex-1" size="lg">
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Spinner variant="light" />
                  <span className="ml-2">Processing...</span>
                </div>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
